import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, HelpCircle } from "lucide-react";
import { CATEGORIES } from "@/lib/api";

const FAQS = {
  "Concrete Admixtures": [
    {
      q: "Why do we use concrete admixtures?",
      a: "To modify concrete properties — such as boosting strength, enhancing workability, controlling setting time, or improving durability under extreme weather.",
    },
    {
      q: "What are the main types of concrete admixtures and which is best?",
      a: "The main types are Lignosulfonates (basic water reduction), Naphthalene-based (medium water reduction with fast slump loss), and Polycarboxylate Ether (PCE) — high water reduction up to 40%, excellent slump retention, and high compressive strength. PCE is the industry standard and our primary recommendation for high-performance concrete.",
    },
    {
      q: "Which admixture type is most affected by clay and excess fine sand?",
      a: "Polycarboxylate Ether (PCE) admixtures are the most sensitive to raw material quality. High clay/silt content and excess fine sand (low Fineness Modulus) heavily absorb free water and PCE chemical polymers, which significantly reduces the admixture's water-reducing efficiency and slump retention compared to Naphthalene or Lignosulfonate types.",
    },
    {
      q: "How do Polycarboxylate Ether (PCE) admixtures work?",
      a: "They disperse cement particles via electrostatic repulsion and steric hindrance, drastically reducing the required water while keeping high slump retention.",
    },
    {
      q: "What causes concrete cracking and how can it be prevented?",
      a: "Cracks occur mainly due to rapid water evaporation (plastic shrinkage), thermal stress, or improper curing conditions. They can be prevented through proper curing, controlled water evaporation, the use of retarders in hot weather, and correct admixture dosing.",
    },
    {
      q: "How does aggregate quality affect admixture performance?",
      a: "High silt content or fine sand (low Fineness Modulus) absorbs free water and chemicals, drastically reducing admixture effectiveness and concrete slump. Clean, well-graded aggregates ensure optimum admixture performance.",
    },
    {
      q: "What are concrete admixtures?",
      a: "Concrete admixtures are chemical products added to concrete before or during mixing to modify its properties — improving workability, strength, setting time, durability and water resistance without changing the basic mix design.",
    },
    {
      q: "What is a superplasticizer (high-range water reducer)?",
      a: "A superplasticizer is a polycarboxylate-based admixture that dramatically increases the flowability of concrete while allowing a large reduction in water. This produces high-strength, dense and durable concrete with excellent workability.",
    },
    {
      q: "How much water reduction can I expect?",
      a: "With HMA polycarboxylate (PCE) superplasticizers you can achieve water reduction of up to 30%, depending on dosage, mix design and site conditions. Normal water reducers typically deliver 5–12%.",
    },
    {
      q: "What dosage should I use?",
      a: "Recommended dosages are stated on each product's Technical Data Sheet (TDS), usually as a range per 100 kg of cement. Always start with a laboratory trial and adjust to your specific cement, aggregates and target slump.",
    },
    {
      q: "Are your admixtures chloride-free?",
      a: "Yes. HMA admixtures are chloride-free and non-corrosive, making them safe for use in reinforced and prestressed concrete structures.",
    },
    {
      q: "Can I use admixtures in hot weather?",
      a: "Absolutely. Set-retarding and slump-retention admixtures are specifically designed for hot-weather concreting and long-haul ready-mix delivery, keeping concrete workable for longer.",
    },
    {
      q: "How do admixtures affect setting time?",
      a: "Accelerators speed up setting and early strength gain (ideal for cold weather and precast), while retarders delay setting to control workability. Standard water reducers have minimal effect on setting time.",
    },
    {
      q: "Can I combine more than one admixture?",
      a: "Yes, but compatibility should always be verified by trial. Never pre-mix admixtures together — add each one separately to the mix to avoid adverse reactions.",
    },
    {
      q: "How should admixtures be stored?",
      a: "Store in original sealed containers in a cool, dry, well-ventilated place away from direct sunlight and frost. Typical shelf life is around 12 months; stir well before use if separation occurs.",
    },
    {
      q: "Do you provide on-site technical support?",
      a: "Yes. Our engineers offer concrete mix optimisation, aggregate analysis and custom formulation development directly at your site to ensure the best performance for your project.",
    },
  ],
  "Cement Grinding Aid": [
    {
      q: "What is a cement grinding aid?",
      a: "A chemical additive introduced into the grinding mill to prevent particle re-agglomeration, increase mill production output, and optimize cement fineness (Blaine).",
    },
    {
      q: "How does a grinding aid increase mill efficiency?",
      a: "It neutralizes electrostatic surface charges on freshly fractured clinker particles, keeping them dispersed, reducing mill coating, and enhancing separator efficiency.",
    },
    {
      q: "Does a grinding aid affect final concrete strength?",
      a: "Yes; advanced grinding aids improve particle size distribution (PSD), boosting 1-day, 7-day, and 28-day compressive strengths of the produced cement.",
    },
    {
      q: "Can grinding aids improve cement pack-set and flowability?",
      a: "Yes, they significantly lower the pack-set index, preventing cement compaction during silo storage, bulk transport, and bag packing.",
    },
    {
      q: "How should grinding aids be dosed and injected into the mill?",
      a: "They should be injected neat (or diluted as recommended) directly onto the clinker feed belt or into the mill inlet stream using precision diaphragm dosing pumps.",
    },
  ],
  "Mortar": [
    {
      q: "What mortar solutions does HMA offer?",
      a: "We offer admixtures for masonry and plastering mortars that improve water retention, workability, adhesion and air content for easier application and better durability.",
    },
    {
      q: "What are the benefits of mortar admixtures?",
      a: "They deliver smoother, more workable mortar, reduced shrinkage cracking, improved bond to substrates and greater resistance to water penetration.",
    },
    {
      q: "Can mortar admixtures improve workability without adding water?",
      a: "Yes. Plasticising mortar admixtures increase workability while keeping the water content controlled, which protects strength and reduces cracking.",
    },
  ],
  "3D Concrete Fence": [
    {
      q: "What is a 3D concrete fence?",
      a: "A 3D concrete fence is a precast, steel-reinforced concrete fence system whose panels feature a three-dimensional relief pattern moulded on both faces — combining strong security with an attractive, decorative appearance from inside and outside.",
    },
    {
      q: "What are the advantages of a 3D concrete fence?",
      a: "It is extremely durable and low-maintenance, resistant to weather, fire and impact, decorative on both sides, and installed as fast modular panels — offering excellent privacy and security with a long service life.",
    },
    {
      q: "What sizes, heights and colours are available?",
      a: "Panels come in standard dimensions and can be stacked between posts to reach the required height. A range of colours and surface finishes is available, with custom sizes and colours produced on request. Exact dimensions and colour for each product are listed on its data sheet.",
    },
    {
      q: "How is a 3D concrete fence installed?",
      a: "Precast H-shaped posts are fixed into foundations, and the 3D panels are simply slotted between them. This modular method makes installation fast and clean, with far less on-site labour and curing time than cast-in-place walls.",
    },
    {
      q: "Are the panels reinforced?",
      a: "Yes. Every 3D concrete fence panel contains a steel-reinforced core that gives it high structural strength, rigidity and resistance to cracking.",
    },
    {
      q: "Is a 3D concrete fence suitable for all weather conditions?",
      a: "Yes. Manufactured from high-quality reinforced concrete, the panels withstand extreme heat, frost, moisture and UV exposure without warping, rusting or fading like metal or timber fencing.",
    },
  ],
};

function FaqItem({ item, isOpen, onToggle, testId }) {
  return (
    <div className="border-b border-slate-200 last:border-0" data-testid={testId}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 py-5 text-left group"
        data-testid={`${testId}-toggle`}
      >
        <span className="font-heading font-medium text-slate-900 group-hover:text-brand-400 transition-colors">{item.q}</span>
        <ChevronDown size={20} className={`shrink-0 text-brand-400 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-sm text-slate-500 leading-relaxed max-w-3xl">{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQ() {
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [openIdx, setOpenIdx] = useState(0);

  const items = FAQS[category] || [];

  const selectCategory = (c) => {
    setCategory(c);
    setOpenIdx(0);
  };

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="faq-page">
      {/* Header */}
      <section className="relative py-20 border-b border-slate-200 grain overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img src="https://images.unsplash.com/photo-1615461476249-718ef8bc369c?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-slate-50/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <span className="text-xs uppercase tracking-widest font-mono text-brand-400 inline-flex items-center gap-2">
            <HelpCircle size={13} /> Support
          </span>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-extrabold text-slate-900">Frequently Asked Questions</h1>
          <p className="mt-4 text-slate-600 max-w-2xl">
            Answers to common questions about our concrete admixtures, cement grinding aids, mortar solutions and precast fence products.
          </p>
        </div>
      </section>

      {/* Tabs */}
      <section className="sticky top-[88px] z-30 bg-slate-50/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => selectCategory(c)}
                data-testid={`faq-tab-${c.replace(/\s/g, "-")}`}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  category === c ? "bg-brand-500 text-white" : "bg-white border border-slate-300 text-slate-600 hover:border-brand-500/50"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Items */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-14">
        <motion.div
          key={category}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="rounded-2xl bg-white border border-slate-200 px-6 sm:px-8"
        >
          {items.map((item, i) => (
            <FaqItem
              key={item.q}
              item={item}
              isOpen={openIdx === i}
              onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
              testId={`faq-item-${i}`}
            />
          ))}
        </motion.div>

        <div className="mt-10 p-7 rounded-2xl bg-brand-500/10 border border-brand-500/20 text-center">
          <h3 className="font-heading text-xl font-semibold text-slate-900">Still have a question?</h3>
          <p className="mt-2 text-sm text-slate-600">Our technical team is ready to help with your specific project.</p>
          <a href="/contact" data-testid="faq-contact-cta" className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-500 hover:bg-brand-600 text-white font-semibold transition-colors">
            Contact our engineers
          </a>
        </div>
      </section>
    </div>
  );
}
